"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Datasheet = void 0;
const command_manager_1 = require("../../command_manager");
const commands_1 = require("../../commands");
const store_1 = require("../../exports/store");
const types_1 = require("../../types");
const _1 = require(".");
const view_1 = require("./view");
class Datasheet {
    /**
     * Create a `Datasheet` instance.
     *
     * @deprecated This constructor is not intended for public use.
     */
    constructor(id, options) {
        this.id = id;
        this.type = types_1.ResourceType.Datasheet;
        const { store, saver, commandManager } = options;
        this.store = store;
        this.saver = saver;
        this._commandManager = commandManager;
    }
    /**
     * The name of this datasheet.
     */
    get name() {
        return store_1.Selectors.getDatasheet(this.store.getState(), this.id).name;
    }
    /**
     * The snapshot data of this datasheet.
     */
    get snapshot() {
        return store_1.Selectors.getSnapshot(this.store.getState(), this.id);
    }
    /**
     * The revision number of this datasheet.
     */
    get revision() {
        return store_1.Selectors.getResourceRevision(this.store.getState(), this.id, types_1.ResourceType.Datasheet);
    }
    /**
     * A map of all fields in the datasheet, including fields hidden in some views.
     */
    get fields() {
        const { fieldMap } = this.snapshot.meta;
        const fields = {};
        for (const fieldId in fieldMap) {
            fields[fieldId] = new _1.Field(fieldMap[fieldId], this.store);
        }
        return fields;
    }
    /**
     * TODO This is a temporary getter needed by front-end. All dependencies of CommandManager in the front-end will be removed in the future.
     *
     * @deprecated
     */
    get commandManager() {
        return this._commandManager;
    }
    /**
     * Perform a command on this datasheet.
     *
     * @param command The command that will be executed.
     * @param saveOptions The options that will be passed to the data saver.
     *
     * @deprecated This method is not intended for public use.
     */
    doCommand(command, saveOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            let beginTime = +new Date();
            const result = this._commandManager.execute(command);
            let endTime = +new Date();
            console.log(`databus datasheet _commandManager.execute, duration [${command}]: ${endTime - beginTime}ms`);
            beginTime = +new Date();
            if (result.result === command_manager_1.ExecuteResult.Success) {
                const saveResult = yield this.saver.saveOps(result.resourceOpsCollects, Object.assign(Object.assign({}, saveOptions), { resource: this, store: this.store }));
                result['saveResult'] = saveResult;
            }
            endTime = +new Date();
            console.log(`databus datasheet saveOps, duration [${command}]: ${endTime - beginTime}ms`);
            return result;
        });
    }
    /**
     * Add records to the datasheet.
     *
     * @param recordOptions Options for adding records.
     * @param saveOptions The options that will be passed to the data saver.
     *
     * @return If the command execution succeeded, the `data` field of the return value is an array of IDs of newly created records.
     */
    addRecords(recordOptions, saveOptions) {
        var _a;
        return this.doCommand({
            cmd: commands_1.CollaCommandName.AddRecords,
            viewId: recordOptions.viewId,
            index: recordOptions.index,
            count: (_a = recordOptions['count']) !== null && _a !== void 0 ? _a : recordOptions['recordValues'].length,
            cellValues: recordOptions['recordValues'],
            groupCellValues: recordOptions.groupCellValues,
            ignoreFieldPermission: recordOptions.ignoreFieldPermission,
            datasheetId: this.id,
        }, saveOptions);
    }
    /**
     * Update existing records in the datasheet.
     *
     * @param recordOptions Options for updating records.
     * @param saveOptions The options that will be passed to the data saver.
     */
    updateRecords(recordOptions, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.SetRecords,
            datasheetId: this.id,
            data: recordOptions,
        }, saveOptions);
    }
    resetRecords(recordMap, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.ResetRecords,
            datasheetId: this.id,
            data: recordMap,
            store: this.store,
        }, saveOptions);
    }
    /**
     * Delete records from the datasheet.
     *
     * @param recordIds Record IDs of records that will be deleted.
     * @param saveOptions The options that will be passed to the data saver.
     */
    deleteRecords(recordIds, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.DeleteRecords,
            data: recordIds,
        }, saveOptions);
    }
    /**
     * Add fields to the datasheet.
     *
     * @param fieldOptions Options for adding fields.
     * @param saveOptions The options that will be passed to the data saver.
     *
     * @return If the command execution succeeded, the `data` field of the return value is the ID of the newly created field.
     */
    addFields(fieldOptions, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.AddFields,
            data: fieldOptions,
            datasheetId: this.id,
        }, saveOptions);
    }
    /**
     * Delete fields from the datasheet.
     *
     * @param fields Options for deleting fields.
     * @param saveOptions The options that will be passed to the data saver.
     */
    deleteFields(fields, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.DeleteField,
            data: fields,
            datasheetId: this.id,
        }, saveOptions);
    }
    /**
     * Update existing fields in the datasheet.
     *
     * @param field New field property.
     * @param saveOptions The options that will be passed to the data saver.
     */
    updateField(field, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.SetFieldAttr,
            fieldId: field.id,
            data: field,
            datasheetId: this.id,
        }, saveOptions);
    }
    /**
     * Add some views to the datasheet.
     *
     * @param views The properties of the views to be added.
     * @param saveOptions The options that will be passed to the data saver.
     */
    addViews(views, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.AddViews,
            data: views,
        }, saveOptions);
    }
    /**
     * Delete some views from the datasheet.
     *
     * @param saveOptions The options that will be passed to the data saver.
     */
    deleteViews(viewIds, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.DeleteViews,
            data: viewIds.map(viewId => ({ viewId })),
        }, saveOptions);
    }
    /**
     * Modify view property.
     *
     * @param views view info
     * @param saveOptions The options that will be passed to the data saver.
     */
    modifyViews(views, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.ModifyViews,
            data: views,
        }, saveOptions);
    }
    /**
     * move view.
     *
     * @param views view info
     * @param saveOptions The options that will be passed to the data saver.
     */
    moveViews(views, saveOptions) {
        return this.doCommand({
            cmd: commands_1.CollaCommandName.MoveViews,
            data: views,
        }, saveOptions);
    }
    getView(idOrOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const state = this.store.getState();
            let viewId;
            if (typeof idOrOptions === 'string') {
                viewId = idOrOptions;
            }
            else if (idOrOptions && 'getViewInfo' in idOrOptions) {
                const { getViewInfo } = idOrOptions;
                const info = yield getViewInfo(state);
                if (!info) {
                    return null;
                }
                return new view_1.View(this, this.store, info);
            }
            const snapshot = store_1.Selectors.getSnapshot(state, this.id);
            if (!snapshot) {
                return null;
            }
            let view;
            if (viewId) {
                view = store_1.Selectors.getViewById(snapshot, viewId);
                if (!view) {
                    return null;
                }
            }
            else {
                view = snapshot.meta.views[0];
            }
            return new view_1.View(this, this.store, {
                property: view,
                fieldMap: snapshot.meta.fieldMap,
            });
        });
    }
}
exports.Datasheet = Datasheet;
//# sourceMappingURL=datasheet.js.map